<template>
  <!-- Already submitted on a previous mount (iframe reloads on every show), so
       greet with a receipt rather than a blank form. "Submit again" re-shows
       the form for a fresh request. -->
  <div v-if="showReceipt" class="text-center">
    <v-icon color="success" size="32" class="mb-2">mdi-check-circle-outline</v-icon>
    <div class="text-body-2 mb-3">{{ t('sales.requestReceived') }}</div>
    <v-btn variant="text" size="small" color="primary" @click="resubmit">{{ t('sales.submitAgain') }}</v-btn>
  </div>

  <!-- Success state for the request just submitted this session. -->
  <div v-else-if="justSubmitted" class="text-center">
    <v-icon color="success" size="32" class="mb-2">mdi-check-circle-outline</v-icon>
    <div class="text-body-2">{{ successMessage }}</div>
  </div>

  <v-form v-else ref="formRef" @submit.prevent="onSubmit">
    <v-text-field
      v-model="name"
      :label="t('sales.name')"
      :rules="nameRules"
      variant="outlined"
      density="comfortable"
      class="mb-3"
      hide-details="auto"
    />
    <v-text-field
      v-model="email"
      :label="t('sales.workEmail')"
      type="email"
      :rules="emailRules"
      variant="outlined"
      density="comfortable"
      class="mb-3"
      hide-details="auto"
    />
    <v-text-field
      v-model="company"
      :label="t('sales.companyTeam')"
      variant="outlined"
      density="comfortable"
      class="mb-3"
      hide-details="auto"
    />
    <v-textarea
      v-model="message"
      :label="t('sales.message')"
      variant="outlined"
      density="comfortable"
      rows="3"
      auto-grow
      counter="2000"
      maxlength="2000"
      class="mb-4"
      hide-details="auto"
    />

    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-3"
    >
      {{ errorMsg }}
    </v-alert>

    <v-btn
      type="submit"
      color="primary"
      block
      :loading="submitting"
      :disabled="!bridge.isReady"
    >
      {{ t('sales.contactSales') }}
    </v-btn>
  </v-form>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed, watch } from 'vue'
import { submitSalesContact } from '../../api/sales.js'
import { useBridgeStore } from '../../stores/bridge.js'
import { useCreditsStore } from '../../stores/credits.js'
import { useRPilotConfig } from '../../composables/useRPilotConfig.js'
import { decodeJwtPayload, pickClaim } from '../../utils/jwt.js'

const props = defineProps({
  // Gate context passed straight to the endpoint: "no_pool" (never provisioned)
  // or "exhausted" (credits used up). Drives the success copy only client-side.
  reason: { type: String, default: 'no_pool' },
})

const emit = defineEmits(['submitted'])

const bridge = useBridgeStore()
const creditsStore = useCreditsStore()
const cfg = useRPilotConfig()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formRef = ref(null)
const name = ref('')
const email = ref('')
const company = ref('')
const message = ref('')

// Prefill name / work email / team from the caller's validated identity on the
// credits-usage response — the gate only renders after that load resolves, so
// this fires with data on first mount. Everything remains editable; the server
// re-validates the token on submit regardless.
watch(
  () => creditsStore.user,
  (u) => {
    if (!u) return
    if (!name.value && u.displayName) name.value = u.displayName
    if (!email.value && EMAIL_RE.test(u.email || '')) email.value = u.email
    if (!company.value && u.teamName) company.value = u.teamName
  },
  { immediate: true }
)

// Fallback prefill from the scopedToken claims (best-effort — RDrive tokens
// often carry only rdrive_user_id, in which case the fields stay blank).
watch(
  () => bridge.scopedToken,
  (token) => {
    const claims = decodeJwtPayload(token)
    if (!claims) return
    if (!name.value) {
      name.value =
        pickClaim(claims, ['rdrive_user_name', 'name', 'full_name']) ||
        [pickClaim(claims, ['given_name', 'first_name']), pickClaim(claims, ['family_name', 'last_name'])]
          .filter(Boolean)
          .join(' ')
    }
    if (!email.value) {
      const candidate = pickClaim(claims, ['rdrive_user_email', 'email', 'upn', 'preferred_username'])
      if (EMAIL_RE.test(candidate)) email.value = candidate
    }
    if (!company.value) company.value = pickClaim(claims, ['rdrive_team_name', 'team_name', 'team', 'company'])
  },
  { immediate: true }
)
const submitting = ref(false)
const errorMsg = ref('')
const justSubmitted = ref(false)
const alreadyKnown = ref(readSubmittedFlag())
// After the user clicks "Submit again" we drop back to the form even though a
// prior submission is on record.
const forceForm = ref(false)

const showReceipt = computed(() => alreadyKnown.value && !forceForm.value && !justSubmitted.value)

const successMessage = computed(() =>
  lastAlreadySubmitted.value
    ? t('sales.alreadyReceived')
    : t('sales.thanks')
)
const lastAlreadySubmitted = ref(false)

const nameRules = [(v) => (!!v && v.trim().length > 0) || t('sales.nameRequired')]
const emailRules = [
  (v) => (!!v && v.trim().length > 0) || t('sales.emailRequired'),
  (v) => EMAIL_RE.test(v || '') || t('sales.emailInvalid'),
]

async function onSubmit() {
  errorMsg.value = ''
  const { valid } = await formRef.value.validate()
  if (!valid) return

  submitting.value = true
  try {
    const res = await submitSalesContact({
      name: name.value.trim(),
      email: email.value.trim(),
      company: company.value.trim() || undefined,
      message: message.value.trim() || undefined,
      reason: props.reason,
    })
    lastAlreadySubmitted.value = res?.alreadySubmitted === true
    writeSubmittedFlag()
    alreadyKnown.value = true
    justSubmitted.value = true
    emit('submitted')
  } catch (e) {
    errorMsg.value = e.message || t('sales.genericError')
  } finally {
    submitting.value = false
  }
}

function resubmit() {
  forceForm.value = true
  justSubmitted.value = false
  errorMsg.value = ''
}

// localStorage flag, keyed by auth context (apiUrl + projectCode) and
// namespaced per the cross-dashboard collision convention. Survives the
// iframe re-mounts so a returning user sees a receipt, not a blank form.
function storageKey() {
  const ns = cfg?.namespace || 'rpilot'
  return `${ns}.sales-contact.${bridge.apiUrl || ''}|${bridge.projectCode || ''}`
}
function readSubmittedFlag() {
  try { return window.localStorage.getItem(storageKey()) === '1' } catch { return false }
}
function writeSubmittedFlag() {
  try { window.localStorage.setItem(storageKey(), '1') } catch { /* sandboxed iframe */ }
}
</script>
